import React, { useState, useEffect, useRef } from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import ReactApexChart from "react-apexcharts";
import apiCall from "../../helpers/api";
import { Card, Row } from "react-bootstrap";
import { IconButton } from "@mui/joy";
import { ContentCopy } from "@mui/icons-material";
import { connect } from "react-redux";
import { STATISTICS_CHART_DATA_API_URL } from "../../configs/url";
import { useSnackbar } from "notistack";
import { CircularProgress } from "@mui/material";
import { QRCode } from "react-qrcode-logo";
import { useTranslation } from "react-i18next";

function Statistics(props) {
  const { enqueueSnackbar } = useSnackbar();
  const qrCodeRef = useRef(null);
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!props?.user || !props?.user?.auth_token) {
      window.location.href = "/login";
    }
    document.title = "Promotions | KPG Partner";
  }, []);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      enqueueSnackbar(t("common_copied"), { variant: "success" });
    });
  };

  const handleFlyerDownload = async (flyer_id) => {
    const canvas = qrCodeRef.current?.querySelector("canvas");
    if (!canvas) {
      enqueueSnackbar("Unable to download image", { variant: "error" });
      console.error("QR Code canvas not found");
      return;
    }

    // Convert canvas to blob
    const dataUrl = canvas.toDataURL("image/png");
    const blob = await (await fetch(dataUrl)).blob();

    const formData = new FormData();
    formData.append("qr_code", blob, "qr.png");
    formData.append("flyer_id", flyer_id);

    apiCall({
      method: "POST",
      url: STATISTICS_CHART_DATA_API_URL,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
      .then(async (response) => {
        if (response.status === 200) {
          enqueueSnackbar("Flyer downloaded successfully", {
            variant: "success",
          });
          const resultBlob = await response.blob();
          const url = window.URL.createObjectURL(resultBlob);
          const link = document.createElement("a");
          link.href = url;
          link.download = "flyer.png";
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      })
      .catch((error) => {
        enqueueSnackbar("Failed to download flyer", { variant: "error" });
        console.error("Download error:", error);
      });
  };

  return (
    <>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          p: 3,
          boxSizing: "border-box",
        }}
      >
        <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold" }}>
          Promotions
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 3,
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Left side - Charts */}
          <Box
            sx={{
              flex: "1 1 70%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minHeight: "600px",
            }}
          >
            {/* Top row */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flex: 1,
              }}
            >
              <Card
                className="promotion-card"
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Card.Body
                  className="stat-card-body"
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 3,
                  }}
                >
                  <div className="stat-icon commission-icon">💵</div>
                  <h6 className="stat-label">
                    Lorem ipsum dolor sit amet consectetur adipisicing elit.
                    Eveniet, enim labore. Velit consectetur nisi commodi amet!
                    Odio atque autem commodi reiciendis illum provident quas,
                    quam quaerat ad aut maiores nihil.
                  </h6>
                  <h3 className="stat-value number-counter">
                    {isLoading ? (
                      <CircularProgress
                        size="sm"
                        sx={{
                          "--CircularProgress-progressColor":
                            "var(--prime-one)",
                        }}
                      />
                    ) : (
                      <>{dashboardData?.new_drivers || 0} </>
                    )}
                  </h3>
                  {props?.user?.affiliate_details?.commission_type ===
                    "fixed" && <p className="stat-subtitle">per renewal</p>}
                </Card.Body>
              </Card>

              <Card
                className="promotion-card"
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Card.Body
                  className="stat-card-body"
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 3,
                  }}
                >
                  <div className="stat-icon commission-icon">💵</div>
                  <h6 className="stat-label">
                    Lorem ipsum dolor sit amet consectetur adipisicing elit.
                    Eveniet, enim labore. Velit consectetur nisi commodi amet!
                    Odio atque autem commodi reiciendis illum provident quas,
                    quam quaerat ad aut maiores nihil.
                  </h6>
                  <h3 className="stat-value number-counter">
                    {isLoading ? (
                      <CircularProgress
                        size="sm"
                        sx={{
                          "--CircularProgress-progressColor":
                            "var(--prime-one)",
                        }}
                      />
                    ) : (
                      <>{dashboardData?.total_earnings || 0} </>
                    )}
                  </h3>
                  {props?.user?.affiliate_details?.commission_type ===
                    "fixed" && <p className="stat-subtitle">this month</p>}
                </Card.Body>
              </Card>
            </Box>

            {/* Bottom row */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flex: 1,
              }}
            >
              <Card
                className="promotion-card"
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Card.Body
                  className="stat-card-body"
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 3,
                  }}
                >
                  <div className="stat-icon commission-icon">📊</div>
                  <h6 className="stat-label">
                    Lorem ipsum dolor sit amet consectetur adipisicing elit.
                    Eveniet, enim labore. Velit consectetur nisi commodi amet!
                    Odio atque autem commodi reiciendis illum provident quas,
                    quam quaerat ad aut maiores nihil.ṣ
                  </h6>
                  <h3 className="stat-value number-counter">
                    {isLoading ? (
                      <CircularProgress
                        size="sm"
                        sx={{
                          "--CircularProgress-progressColor":
                            "var(--prime-one)",
                        }}
                      />
                    ) : (
                      <>{dashboardData?.active_referrals || 0} </>
                    )}
                  </h3>
                  {props?.user?.affiliate_details?.commission_type ===
                    "fixed" && (
                    <p className="stat-subtitle">currently active</p>
                  )}
                </Card.Body>
              </Card>

              <Card
                className="promotion-card"
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Card.Body
                  className="stat-card-body"
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 3,
                  }}
                >
                  <div className="stat-icon commission-icon">🎯</div>
                  <h6 className="stat-label">
                    Lorem ipsum dolor sit amet consectetur adipisicing elit.
                    Eveniet, enim labore. Velit consectetur nisi commodi amet!
                    Odio atque autem commodi reiciendis illum provident quas,
                    quam quaerat ad aut maiores nihil.
                  </h6>
                  <h3 className="stat-value number-counter">
                    {isLoading ? (
                      <CircularProgress
                        size="sm"
                        sx={{
                          "--CircularProgress-progressColor":
                            "var(--prime-one)",
                        }}
                      />
                    ) : (
                      <>{dashboardData?.conversion_rate || 0}% </>
                    )}
                  </h3>
                  {props?.user?.affiliate_details?.commission_type ===
                    "fixed" && <p className="stat-subtitle">success rate</p>}
                </Card.Body>
              </Card>
            </Box>
          </Box>

          {/* Right side - QR Code Section */}
          <Box sx={{ flex: "0 0 500px", minWidth: "500px" }}>
            <Paper
              className="promotion-card"
              elevation={2}
              sx={{
                p: 3,
                borderRadius: "16px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 2,
              }}
            >
              <Card className="referral-info-card" sx={{ width: "100%" }}>
                <Card.Header className="referral-info-header">
                  <Row className="align-items-center">
                    <div className="col-12">
                      <h5 className="referral-info-title">
                        Referral Information
                      </h5>
                    </div>
                  </Row>
                </Card.Header>
                <Card.Body className="referral-info-body">
                  <Box className="referral-info-content">
                    <Box
                      className="referral-link-box"
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        gap: 1,
                        wordBreak: "break-word",
                      }}
                    >
                      <Typography
                        variant="body2"
                        className="referral-label"
                        sx={{ minWidth: "60px" }}
                      >
                        Ref link:
                      </Typography>

                      <Typography
                        variant="body2"
                        className="referral-value"
                        sx={{
                          wordBreak: "break-word",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        https://drivers.delivery-kpg.com/driver-agent-form?ref=
                        {props?.user?.referral_code}
                      </Typography>

                      <IconButton
                        onClick={() =>
                          handleCopy(
                            `https://drivers.delivery-kpg.com/driver-agent-form?ref=${props?.user?.referral_code}`
                          )
                        }
                        aria-label="Copy ref link"
                        size="small"
                        className="copy-button"
                        sx={{ alignSelf: "start", p: 0.5 }}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Card.Body>
              </Card>

              {/* QR Code */}
              <Typography variant="h6" sx={{ mt: 1, textAlign: "center" }}>
                Referral QR Code
              </Typography>

              <div ref={qrCodeRef}>
                <QRCode
                  value={`https://drivers.delivery-kpg.com/driver-agent-form?ref=${props?.user?.referral_code}`}
                  size={200}
                  level="H"
                />
              </div>

              <Typography
                variant="body2"
                sx={{ mt: 1, textAlign: "center", color: "text.secondary" }}
              >
                Scan to refer new drivers
              </Typography>
            </Paper>
          </Box>
        </Box>
      </Box>
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.user.user,
});

const mapDispatchToProps = () => {};

export default connect(mapStateToProps, mapDispatchToProps)(Statistics);
